import { createHash } from 'node:crypto';

export const AUTH_DIAGNOSTICS_SCHEMA_VERSION = 1 as const;
export const MAX_AUTH_DIAGNOSTIC_EVENTS = 80;
export const MAX_PENDING_AUTH_DIAGNOSTIC_CAPTURES = 80;

export type AuthProvider = 'firebase-identity-toolkit' | 'firebase-secure-token';

export type AuthTokenEvidence = {
  /** Irreversible SHA-256 prefix used only to compare requests within diagnostics. */
  fingerprint: string;
  issuerFingerprint: string | null;
  audienceFingerprint: string | null;
  subjectFingerprint: string | null;
  issuedAt: number | null;
  expiresAt: number | null;
  authTime: number | null;
};

export type AuthRequestEvidence = {
  provider: AuthProvider;
  endpoint: string;
  method: string;
  queryParameterNames: string[];
  hasApiKey: boolean;
  apiKeyFingerprint: string | null;
  bodyFieldNames: string[];
  token: AuthTokenEvidence | null;
};

type BrowserAuthDiagnosticBase = {
  sequence: number;
  timestamp: string;
  contextCreatedAtMs: number | null;
  pageId: number | null;
  recoveryGeneration: number;
  browserId: string | null;
  leaseId: string | null;
};

export type BrowserAuthDiagnosticEvent =
  | (BrowserAuthDiagnosticBase & {
      kind: 'storage_restore';
      phase: 'started' | 'succeeded' | 'failed';
      targetOrigin: string | null;
      stateFingerprint: string;
      expectedDatabases: number;
      expectedDatabaseNames: string[];
      expectedStores: number;
      expectedStoreNames: string[];
      expectedEntries: number;
      expectedOrigins: number;
      expectedLocalStorageEntries: number;
      matchedLocalStorageEntries?: number;
      restoredDatabases?: number;
      restoredStores?: number;
      restoredEntries?: number;
      skippedDatabases?: Array<{ name: string; error: string }>;
      skippedStores?: Array<{ database: string; store: string; error: string }>;
      durationMs?: number;
      error?: string;
    })
  | (BrowserAuthDiagnosticBase & {
      kind: 'auth_request';
      phase: 'started' | 'completed' | 'failed';
      requestId: string;
      evidence: AuthRequestEvidence;
      status?: number;
      statusText?: string;
      durationMs?: number;
      providerErrorCode?: string | null;
      remoteAddress?: string | null;
      protocol?: string | null;
      error?: string;
    })
  | (BrowserAuthDiagnosticBase & {
      kind: 'browser_lifecycle';
      event:
        | 'connected'
        | 'page_closed'
        | 'page_crashed'
        | 'context_closed'
        | 'browser_disconnected'
        | 'recovery_started'
        | 'recovery_completed'
        | 'recovery_failed'
        | 'context_swapped';
      url: string | null;
      reason?: string;
    })
  | (BrowserAuthDiagnosticBase & {
      kind: 'navigation';
      url: string;
    });

export type BrowserAuthDiagnostics = {
  schemaVersion: typeof AUTH_DIAGNOSTICS_SCHEMA_VERSION;
  capturedAt: string;
  current: {
    connected: boolean;
    contextCreatedAtMs: number | null;
    contextAgeMs: number | null;
    pageId: number | null;
    url: string | null;
    recoveryGeneration: number;
    browserId: string | null;
    leaseId: string | null;
  };
  events: BrowserAuthDiagnosticEvent[];
};

const fingerprint = (value: string): string =>
  createHash('sha256').update(value).digest('hex').slice(0, 16);

export const hasAuthDiagnosticCaptureCapacity = (pendingCaptures: number): boolean =>
  pendingCaptures < MAX_PENDING_AUTH_DIAGNOSTIC_CAPTURES;

const safeNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const safeClaim = (value: unknown): string | null => {
  if (typeof value === 'string' && value.length > 0) return value.slice(0, 256);
  if (Array.isArray(value)) {
    const strings = value.filter((item): item is string => typeof item === 'string');
    return strings.length > 0 ? strings.join(',').slice(0, 256) : null;
  }
  return null;
};

function decodeJwtEvidence(token: string): AuthTokenEvidence {
  let payload: Record<string, unknown> | null = null;
  const parts = token.split('.');
  if (parts.length === 3) {
    try {
      const decoded: unknown = JSON.parse(Buffer.from(parts[1], 'base64url').toString('utf8'));
      if (decoded && typeof decoded === 'object' && !Array.isArray(decoded)) {
        payload = decoded as Record<string, unknown>;
      }
    } catch {
      payload = null;
    }
  }

  const subject = safeClaim(payload?.sub);
  const issuer = safeClaim(payload?.iss);
  const audience = safeClaim(payload?.aud);
  return {
    fingerprint: fingerprint(token),
    issuerFingerprint: issuer ? fingerprint(issuer) : null,
    audienceFingerprint: audience ? fingerprint(audience) : null,
    subjectFingerprint: subject ? fingerprint(subject) : null,
    issuedAt: safeNumber(payload?.iat),
    expiresAt: safeNumber(payload?.exp),
    authTime: safeNumber(payload?.auth_time),
  };
}

function parseBody(postData: string | null): {
  fieldNames: string[];
  token: string | null;
} {
  if (!postData) return { fieldNames: [], token: null };

  try {
    const parsed: unknown = JSON.parse(postData);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const record = parsed as Record<string, unknown>;
      const token =
        typeof record.idToken === 'string'
          ? record.idToken
          : typeof record.refreshToken === 'string'
            ? record.refreshToken
            : null;
      return { fieldNames: Object.keys(record).sort(), token };
    }
  } catch {
    // Firebase's secure-token endpoint uses form encoding rather than JSON.
  }

  try {
    const params = new URLSearchParams(postData);
    const token = params.get('id_token') ?? params.get('refresh_token');
    return { fieldNames: [...new Set(params.keys())].sort(), token };
  } catch {
    return { fieldNames: [], token: null };
  }
}

/**
 * Return a secret-free description only for known Firebase authentication calls.
 * Raw URLs, API keys, tokens, and request bodies never leave this function.
 */
export function inspectAuthRequest(args: {
  url: string;
  method: string;
  postData: string | null;
}): AuthRequestEvidence | null {
  let url: URL;
  try {
    url = new URL(args.url);
  } catch {
    return null;
  }

  const provider: AuthProvider | null =
    url.hostname === 'identitytoolkit.googleapis.com'
      ? 'firebase-identity-toolkit'
      : url.hostname === 'securetoken.googleapis.com'
        ? 'firebase-secure-token'
        : null;
  if (!provider) return null;

  const apiKey = url.searchParams.get('key');
  const body = parseBody(args.postData);
  return {
    provider,
    endpoint: url.pathname,
    method: args.method,
    queryParameterNames: [...new Set(url.searchParams.keys())].sort(),
    hasApiKey: apiKey !== null && apiKey.length > 0,
    apiKeyFingerprint: apiKey ? fingerprint(apiKey) : null,
    bodyFieldNames: body.fieldNames,
    token: body.token ? decodeJwtEvidence(body.token) : null,
  };
}

/** Extract only stable provider error codes such as MISSING_PROJECT_ID. */
export function inspectAuthProviderError(responseBody: string): string | null {
  try {
    const parsed: unknown = JSON.parse(responseBody);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const error = (parsed as Record<string, unknown>).error;
    if (!error || typeof error !== 'object' || Array.isArray(error)) return null;
    const message = (error as Record<string, unknown>).message;
    if (typeof message !== 'string') return null;
    const candidate = message.split(':', 1)[0]?.trim();
    return candidate && /^[A-Z][A-Z0-9_]{2,63}$/.test(candidate) ? candidate : null;
  } catch {
    return null;
  }
}

/** Strip query strings (including Firebase API keys) and fragments from diagnostic URLs. */
export function sanitizeDiagnosticUrl(raw: string): string | null {
  try {
    const url = new URL(raw);
    return `${url.origin}${url.pathname}`;
  } catch {
    return null;
  }
}

/** Remove URL query secrets and host-local file paths from retained error text. */
export function sanitizeDiagnosticMessage(raw: string): string {
  return raw
    .replace(/https?:\/\/[^\s"'<>]+/g, (candidate) => sanitizeDiagnosticUrl(candidate) ?? '[url]')
    .replace(/(^|[\s("'])\/(?:Users|private|tmp|var|home|workspace)\/[^\s)"']+/g, '$1[path]')
    .replace(/\b[A-Za-z]:\\[^\s)"']+/g, '[path]')
    .slice(0, 512);
}

export function fingerprintStorageState(raw: string): string {
  return fingerprint(raw);
}
